import { useEffect, useState } from 'react';
import { listWarehouses } from '../services/WarehousesService';
import { stockEnAlmacen } from '../services/BooksService';
import { getWarehouseId } from '../services/TerminalService';

export interface WarehouseStock {
    warehouseId: string;
    name: string;
    stock: number;
}

/**
 * Existencias del libro en los DEMAS almacenes/librerias del grupo, para
 * ofrecerlas antes que pedirselo a un proveedor: si lo tiene la de al lado, el
 * cliente lo tiene en un dia y no en tres semanas.
 *
 * Solo se consulta cuando el cajero despliega un libro sin stock (`enabled`),
 * no por cada resultado de la busqueda: es una llamada por almacen.
 *
 * Si falla, devuelve lista vacia y la pantalla se queda como estaba. No poder
 * mirar el stock de otro almacen no puede impedir pedirselo a un proveedor.
 */
export const useOtherWarehousesStock = (isbnOrId: string | null, enabled: boolean) => {
    const [almacenes, setAlmacenes] = useState<WarehouseStock[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!enabled || !isbnOrId) {
            setAlmacenes([]);
            setIsLoading(false);
            return;
        }

        // Mismo patron que useBookSearch: `cancelled` evita que la respuesta de
        // un libro pise a la de otro desplegado despues.
        let cancelled = false;
        setIsLoading(true);
        setAlmacenes([]);

        (async () => {
            try {
                const propio = getWarehouseId();
                const otros = (await listWarehouses()).filter(w => String(w.id) !== String(propio));

                const conStock: WarehouseStock[] = [];
                for (const w of otros) {
                    const stock = await stockEnAlmacen(isbnOrId, w.id);
                    if (cancelled) return;
                    if (stock > 0) conStock.push({ warehouseId: w.id, name: w.name, stock });
                }

                if (cancelled) return;
                conStock.sort((a, b) => b.stock - a.stock);
                setAlmacenes(conStock);
                setIsLoading(false);
            } catch {
                if (cancelled) return;
                setAlmacenes([]);
                setIsLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [isbnOrId, enabled]);

    return { almacenes, isLoading };
};
